import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useDeleteAvatarMutation } from '@/services/avatarApi';

import { EditorShell } from './components/EditorShell';
import { DraftResumePrompt } from './components/DraftResumePrompt';
import { Step1Concept, step1Valid } from './steps/Step1Concept';
import { Step2RenderSetup, step2Valid } from './steps/Step2RenderSetup';
import { Step3Generate, step3Valid } from './steps/Step3Generate';
import { Step4MotionBrief, step4Valid } from './steps/Step4MotionBrief';
import { Step5EngineQuality, step5Valid } from './steps/Step5EngineQuality';
import { Step6Confirm } from './steps/Step6Confirm';
import { nextStep, prevStep } from './constants';
import { useEditorState } from './hooks/useEditorState';
import type { StepId } from './types';

/**
 * Route component for /avatars/editor. Renders the active step inside an
 * EditorShell that owns the header, progress bar, sticky footer, and the
 * sidebar preview.
 */
export function AvatarEditor() {
  const navigate = useNavigate();
  const { t } = useTranslation('avatars');
  const { state, update, merge, reset, loadDraft, hasDraft } = useEditorState();

  // On mount, check for a draft. If present, show the resume prompt before
  // the wizard. The prompt is dismissed by either loading the draft or
  // discarding it (which resets to a fresh state).
  const [draftDecision, setDraftDecision] = useState<'pending' | 'resolved'>(() =>
    hasDraft() ? 'pending' : 'resolved',
  );

  // Validity per step gates the Next button. Confirm has its own internal
  // action button, so its Next disabled doesn't matter.
  const stepIsValid = useCallback((s: StepId): boolean => {
    switch (s) {
      case 'concept':     return step1Valid(state);
      case 'renderSetup': return step2Valid(state);
      case 'generate':    return step3Valid(state);
      case 'motion':      return step4Valid(state);
      case 'engine':      return step5Valid(state);
      case 'confirm':     return true;
      default:            return false;
    }
  }, [state]);

  const goNext = useCallback(() => {
    const next = nextStep(state.currentStep, state.sourceMode);
    if (next) update('currentStep', next);
  }, [state.currentStep, state.sourceMode, update]);

  const goBack = useCallback(() => {
    const prev = prevStep(state.currentStep, state.sourceMode);
    if (prev) update('currentStep', prev);
  }, [state.currentStep, state.sourceMode, update]);

  const onClose = useCallback(() => {
    // Draft remains in localStorage — the user can resume next time.
    navigate('/');
  }, [navigate]);

  const onComplete = useCallback(() => {
    reset();
    navigate(`/avatars/${state.avatarId}`);
  }, [navigate, reset, state.avatarId]);

  const [deleteAvatar] = useDeleteAvatarMutation();

  // Step 3 exit ramps — let the user accept the 2D image and exit the
  // wizard without going through the animation pipeline, or discard the
  // generated avatar entirely.
  const onSaveAndExit = useCallback(() => {
    if (!state.avatarId) return;
    const id = state.avatarId;
    reset();
    navigate(`/avatars/${id}`);
  }, [navigate, reset, state.avatarId]);

  const onDiscard = useCallback(async () => {
    if (!state.avatarId) {
      reset();
      navigate('/');
      return;
    }
    try {
      await deleteAvatar(state.avatarId).unwrap();
    } catch (err) {
      console.warn('[AvatarEditor] discard delete failed', err);
    }
    reset();
    navigate('/');
  }, [deleteAvatar, navigate, reset, state.avatarId]);

  // Resume / discard handlers for the draft prompt.
  const handleResume = useCallback(() => {
    loadDraft();
    setDraftDecision('resolved');
  }, [loadDraft]);
  const handleDiscard = useCallback(() => {
    reset();
    setDraftDecision('resolved');
  }, [reset]);

  if (draftDecision === 'pending') {
    return (
      <DraftResumePrompt
        startedAt={state.startedAt}
        onResume={handleResume}
        onDiscard={handleDiscard}
      />
    );
  }

  return (
    <EditorShell
      state={state}
      onClose={onClose}
      onBack={prevStep(state.currentStep, state.sourceMode) ? goBack : undefined}
      onNext={state.currentStep === 'confirm' ? undefined : goNext}
      nextDisabled={!stepIsValid(state.currentStep)}
      nextLabel={state.currentStep === 'engine' ? t('editor.shell.review') : undefined}
    >
      {state.currentStep === 'concept' && (
        <Step1Concept state={state} update={update} merge={merge} />
      )}
      {state.currentStep === 'renderSetup' && (
        <Step2RenderSetup state={state} update={update} />
      )}
      {state.currentStep === 'generate' && (
        <Step3Generate
          state={state}
          update={update}
          merge={merge}
          onContinue={goNext}
          onSaveAndExit={onSaveAndExit}
          onDiscard={onDiscard}
        />
      )}
      {state.currentStep === 'motion' && (
        <Step4MotionBrief state={state} update={update} />
      )}
      {state.currentStep === 'engine' && (
        <Step5EngineQuality state={state} update={update} merge={merge} />
      )}
      {state.currentStep === 'confirm' && (
        <Step6Confirm
          state={state}
          update={update}
          merge={merge}
          onComplete={onComplete}
          onBackToEngine={() => update('currentStep', 'engine')}
        />
      )}
    </EditorShell>
  );
}

export default AvatarEditor;
